import Phaser from 'phaser';
import { findPath, adjacentWalkable } from '../world/pathfinding';
import { faceMove, faceIdle, type Facing } from '../world/playerFacing';
import { TX, AUDIO } from '../registry/keys';
import { CONFIG } from '../data/config';
import { playSfx } from '../audio/sfx';
import { tileKey, type TileXY } from '../types';
import { getHat, hatTexture, getClaraOutfit, claraOutfitTexture } from '../data/hats';
import { store } from '../state/store';
import { HOUSE_TIERS } from '../data/house';
import { getDecoration } from '../data/decorations';
import type { WorldScene } from './WorldScene';
import type { UIScene } from './UIScene';

const TS = CONFIG.tileSize;
const ROOM_W = 11;
const ROOM_H = 8;
const DOOR: TileXY = { x: 5, y: 7 };
const ENTRY: TileXY = { x: 5, y: 6 };
const BED: TileXY = { x: 1, y: 2 };
const STOVE: TileXY = { x: 3, y: 2 };
const WORKBENCH: TileXY = { x: 7, y: 2 };
const CHEST: TileXY = { x: 9, y: 2 };
const LADDER: TileXY = { x: 1, y: 6 };
const ORIGIN_Y = 0.7;
// per-tier tints so the room visibly changes as you upgrade (cottage warm → castle stone)
const FLOOR_TINT = [0xffffff, 0xf6efe0, 0xf2ead8, 0xe6e2ea, 0xdee2ec, 0xd6dbe6];
const WALL_TINT = [0xffffff, 0xf0e6d2, 0xeadcc4, 0xd6cfc2, 0xc4c6cc, 0xb6bec8];

/** The inside of the house: tap-to-move, a bed (sleep), a chest (storage), a door (leave). */
export class InteriorScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private blocked = new Set<string>();
  private path: TileXY[] = [];
  private pendingArrival: (() => void) | null = null;
  private playerTile: TileXY = { ...ENTRY };
  private leaving = false;
  private hat?: Phaser.GameObjects.Image;
  private lastFacing: Facing = 'down';
  private decoSprites = new Map<string, Phaser.GameObjects.Image>();

  constructor() {
    super('Interior');
  }

  private get ui(): UIScene {
    return this.scene.get('UI') as UIScene;
  }
  private get world(): WorldScene {
    return this.scene.get('World') as WorldScene;
  }

  create(): void {
    this.leaving = false;
    this.blocked.clear();
    this.path = [];
    this.decoSprites.clear();
    this.cameras.main.setBackgroundColor('#1a140f');
    const short = Math.min(this.scale.width, this.scale.height);
    this.cameras.main.setZoom(Phaser.Math.Clamp(Math.round(short / (ROOM_W * TS)), CONFIG.minZoom, CONFIG.maxZoom));
    this.cameras.main.centerOn((ROOM_W * TS) / 2, (ROOM_H * TS) / 2);

    this.buildRoom();
    this.renderInteriorDecor();

    this.player = this.add
      .sprite(ENTRY.x * TS + TS / 2, ENTRY.y * TS + TS, TX.player, 0)
      .setOrigin(0.5, ORIGIN_Y);
    this.player.setDepth(ENTRY.y * TS + TS);
    this.playerTile = { ...ENTRY };
    this.applyHat();

    this.add
      .text(this.scale.width / 2, 8, `Your ${HOUSE_TIERS[store.get().houseLevel]?.name ?? 'home'} — place décor from your bag · tap the door`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#e8dcc0',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setResolution(2)
      .setDepth(1000);

    this.input.on('pointerdown', this.onTap, this);
  }

  private buildRoom(): void {
    const tier = store.get().houseLevel;
    const floorTint = FLOOR_TINT[tier] ?? 0xffffff;
    const wallTint = WALL_TINT[tier] ?? 0xffffff;
    for (let y = 0; y < ROOM_H; y++) {
      for (let x = 0; x < ROOM_W; x++) {
        const wall = y <= 1 || x === 0 || x === ROOM_W - 1 || y === ROOM_H - 1;
        const door = x === DOOR.x && y === DOOR.y;
        this.add.image(x * TS, y * TS, TX.intFloor).setOrigin(0, 0).setDepth(0).setTint(floorTint);
        if (door) {
          this.add.image(x * TS, y * TS, TX.intDoor).setOrigin(0, 0).setDepth(1);
        } else if (wall) {
          this.add.image(x * TS, y * TS, TX.intWall).setOrigin(0, 0).setDepth(1).setTint(wallTint);
          this.blocked.add(tileKey(x, y));
        }
      }
    }
    this.add.image(BED.x * TS, BED.y * TS, TX.bed).setOrigin(0, 0).setDepth((BED.y + 2) * TS);
    this.blocked.add(tileKey(BED.x, BED.y));
    this.blocked.add(tileKey(BED.x, BED.y + 1));
    this.add.image(CHEST.x * TS + TS / 2, CHEST.y * TS + TS, TX.chest).setOrigin(0.5, 1).setDepth(CHEST.y * TS + TS);
    this.blocked.add(tileKey(CHEST.x, CHEST.y));
    this.add.image(WORKBENCH.x * TS, WORKBENCH.y * TS, TX.workbench).setOrigin(0, 0).setDepth(WORKBENCH.y * TS + TS);
    this.blocked.add(tileKey(WORKBENCH.x, WORKBENCH.y));
    this.add.image(STOVE.x * TS, STOVE.y * TS, TX.stove).setOrigin(0, 0).setDepth(STOVE.y * TS + TS);
    this.blocked.add(tileKey(STOVE.x, STOVE.y));
    this.add.image(LADDER.x * TS, LADDER.y * TS, TX.ladder).setOrigin(0, 0).setDepth(1); // down to the mine
    // station mats + labels — distinct from the floor, and you can read what each one is
    const mat = (tx: number, ty: number, w: number, h: number, color: number): void => {
      this.add.rectangle(tx * TS, ty * TS, TS * w, TS * h, color, 0.3).setOrigin(0, 0).setDepth(0.4);
    };
    const slabel = (tx: number, ty: number, text: string): void => {
      this.add
        .text(tx * TS + TS / 2, ty * TS - 1, text, { fontFamily: 'monospace', fontSize: '8px', color: '#ffe9a8', backgroundColor: '#000000aa', padding: { x: 2, y: 1 } })
        .setOrigin(0.5, 0)
        .setResolution(2)
        .setDepth(80);
    };
    mat(BED.x, BED.y, 1, 2, 0x8a5ea1);
    mat(STOVE.x, STOVE.y, 1, 1, 0xc06030);
    mat(WORKBENCH.x, WORKBENCH.y, 1, 1, 0x9a6b3f);
    mat(CHEST.x, CHEST.y, 1, 1, 0xc9a14a);
    mat(LADDER.x, LADDER.y, 1, 1, 0x6e747c);
    slabel(STOVE.x, 3, 'Cook');
    slabel(WORKBENCH.x, 3, 'Craft');
    slabel(CHEST.x, 3, 'Chest');
    slabel(BED.x, 4, 'Bed');
    slabel(LADDER.x, LADDER.y + 1, 'Mine');
    // framed family photo on the back wall — tap to view
    if (this.textures.exists(TX.photo)) {
      const px0 = (ROOM_W / 2) * TS;
      this.add.rectangle(px0, 2, 32, 40, 0x6a4a2e).setOrigin(0.5, 0).setDepth(2);
      this.add.rectangle(px0, 4, 28, 36, 0xf4ecdc).setOrigin(0.5, 0).setDepth(2.1);
      this.add.image(px0, 6, TX.photo).setOrigin(0.5, 0).setDisplaySize(24, 32).setDepth(2.2);
      this.add
        .rectangle(px0, 0, 38, 46, 0xffffff, 0)
        .setOrigin(0.5, 0)
        .setDepth(3)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => this.ui.showPhoto());
    }
    // Clara's portrait on the wall — reflects her current outfit
    {
      const cx = (ROOM_W / 2) * TS + 40;
      this.add.rectangle(cx, 6, 22, 26, 0x6a4a2e).setOrigin(0.5, 0).setDepth(2);
      this.add.rectangle(cx, 8, 18, 22, 0xcfe4ef).setOrigin(0.5, 0).setDepth(2.1);
      this.add.image(cx, 17, TX.clara).setOrigin(0.5, 0.5).setDepth(2.2).setScale(1.1);
      const otex = claraOutfitTexture(getClaraOutfit());
      if (otex) this.add.image(cx, 13, otex).setOrigin(0.5, 0.5).setDepth(2.3).setScale(0.7);
    }
    // renovation blueprint on the wall — tap to upgrade the house (cottage → castle)
    {
      const rx = (ROOM_W / 2) * TS - 40;
      this.add.rectangle(rx, 6, 22, 26, 0x4a5a6a).setOrigin(0.5, 0).setDepth(2);
      this.add.image(rx, 11, TX.blueprint).setOrigin(0.5, 0).setScale(1.2).setDepth(2.2);
      this.add.text(rx, 26, '🔨', { fontFamily: 'monospace', fontSize: '9px' }).setOrigin(0.5, 0).setResolution(2).setDepth(2.2);
      this.add
        .rectangle(rx, 4, 26, 32, 0xffffff, 0)
        .setOrigin(0.5, 0)
        .setDepth(3)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => this.ui.openHouseUpgrade());
    }
  }

  private isBlocked = (x: number, y: number): boolean =>
    x < 0 || y < 0 || x >= ROOM_W || y >= ROOM_H || this.blocked.has(tileKey(x, y));

  private onTap(pointer: Phaser.Input.Pointer): void {
    if (this.leaving || this.ui.isModalOpen() || this.ui.consumesPointer(pointer.x, pointer.y)) return;
    const tile = { x: Math.floor(pointer.worldX / TS), y: Math.floor(pointer.worldY / TS) };
    if (tile.x < 0 || tile.y < 0 || tile.x >= ROOM_W || tile.y >= ROOM_H) return;

    // decorate: place a selected decoration on free floor, or pick a placed one back up
    const decoId = this.ui.selectedDecoId;
    if (decoId) {
      const dd = getDecoration(decoId);
      if (dd && this.canPlaceInterior(tile, dd.w, dd.h)) {
        return this.routeTo(tile, () => this.placeInteriorDeco(decoId, tile));
      }
    }
    if (!decoId && this.decoSprites.has(tileKey(tile.x, tile.y))) {
      return this.routeToInteractable(tile, () => this.pickUpInteriorDeco(tile));
    }

    if (tile.x === BED.x && (tile.y === BED.y || tile.y === BED.y + 1)) {
      return this.routeToInteractable(BED, () => this.ui.confirmSleep(() => this.sleep()));
    }
    if (tile.x === CHEST.x && tile.y === CHEST.y) {
      return this.routeToInteractable(CHEST, () => this.ui.openStorage());
    }
    if (tile.x === WORKBENCH.x && tile.y === WORKBENCH.y) {
      return this.routeToInteractable(WORKBENCH, () => this.ui.openCrafting());
    }
    if (tile.x === STOVE.x && tile.y === STOVE.y) {
      return this.routeToInteractable(STOVE, () => this.ui.openCooking());
    }
    if (tile.x === LADDER.x && tile.y === LADDER.y) {
      return this.routeTo(tile, () => this.enterMine());
    }
    if (tile.x === DOOR.x && tile.y === DOOR.y) {
      return this.routeTo(tile, () => this.leave());
    }
    if (!this.isBlocked(tile.x, tile.y)) this.routeTo(tile, null);
  }

  private routeTo(tile: TileXY, action: (() => void) | null): void {
    if (this.playerTile.x === tile.x && this.playerTile.y === tile.y) {
      action?.();
      return;
    }
    const path = findPath(this.playerTile, tile, ROOM_W, ROOM_H, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private routeToInteractable(target: TileXY, action: () => void): void {
    if (Math.abs(this.playerTile.x - target.x) + Math.abs(this.playerTile.y - target.y) <= 1) {
      action();
      return;
    }
    const adj = adjacentWalkable(target, this.playerTile, ROOM_W, ROOM_H, this.isBlocked);
    if (!adj) return;
    const path = findPath(this.playerTile, adj, ROOM_W, ROOM_H, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private sleep(): void {
    if (this.leaving) return;
    this.leaving = true;
    this.scene.stop();
    this.scene.resume('World');
    this.world.sleepFromInterior();
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    this.world.exitPlacement(); // clear any decoration-placement state before returning outside
    this.ui.clearSelection();
    playSfx(this, AUDIO.pop);
    this.scene.stop();
    this.scene.resume('World');
  }

  private renderInteriorDecor(): void {
    for (const d of store.get().interiorDecor) {
      const def = getDecoration(d.decoId);
      if (!def) continue;
      const img = this.add.image(d.x * TS, d.y * TS, def.texture).setOrigin(0, 0).setDepth(def.ground ? 1 : (d.y + def.h) * TS);
      for (let dx = 0; dx < def.w; dx++) for (let dy = 0; dy < def.h; dy++) this.decoSprites.set(tileKey(d.x + dx, d.y + dy), img);
    }
  }

  private canPlaceInterior(tile: TileXY, w = 1, h = 1): boolean {
    for (let dx = 0; dx < w; dx++) {
      for (let dy = 0; dy < h; dy++) {
        const tx = tile.x + dx;
        const ty = tile.y + dy;
        if (tx <= 0 || tx >= ROOM_W - 1 || ty <= 1 || ty >= ROOM_H - 1) return false;
        if (this.blocked.has(tileKey(tx, ty))) return false;
        if (tx === DOOR.x && ty === DOOR.y) return false;
        if (tx === LADDER.x && ty === LADDER.y) return false;
        if (this.decoSprites.has(tileKey(tx, ty))) return false;
      }
    }
    return true;
  }

  private placeInteriorDeco(decoId: string, tile: TileXY): void {
    const def = getDecoration(decoId);
    if (!def || store.countItem(decoId) <= 0 || !this.canPlaceInterior(tile, def.w, def.h)) return;
    store.removeItem(decoId, 1);
    store.update((s) => s.interiorDecor.push({ decoId, x: tile.x, y: tile.y, growth: undefined }));
    const img = this.add.image(tile.x * TS, tile.y * TS, def.texture).setOrigin(0, 0).setDepth(def.ground ? 1 : (tile.y + def.h) * TS);
    for (let dx = 0; dx < def.w; dx++) for (let dy = 0; dy < def.h; dy++) this.decoSprites.set(tileKey(tile.x + dx, tile.y + dy), img);
    playSfx(this, AUDIO.pop);
    this.ui.showToast(`Placed ${def.name}`, 'good');
    if (store.countItem(decoId) <= 0) {
      this.world.exitPlacement();
      this.ui.clearSelection();
    }
  }

  private pickUpInteriorDeco(tile: TileXY): void {
    const idx = store.get().interiorDecor.findIndex((d) => {
      const def = getDecoration(d.decoId);
      return tile.x >= d.x && tile.x < d.x + (def?.w ?? 1) && tile.y >= d.y && tile.y < d.y + (def?.h ?? 1);
    });
    if (idx < 0) return;
    const d = store.get().interiorDecor[idx]!;
    const def = getDecoration(d.decoId);
    this.decoSprites.get(tileKey(d.x, d.y))?.destroy();
    for (let dx = 0; dx < (def?.w ?? 1); dx++) for (let dy = 0; dy < (def?.h ?? 1); dy++) this.decoSprites.delete(tileKey(d.x + dx, d.y + dy));
    store.update((s) => s.interiorDecor.splice(idx, 1));
    store.addItem(d.decoId, 1);
    playSfx(this, AUDIO.pop);
    this.ui.showToast(`Picked up ${def?.name ?? 'decoration'}`, 'info');
  }

  private enterMine(): void {
    this.scene.launch('Mine');
    this.scene.pause();
  }

  applyHat(): void {
    if (this.hat) {
      this.hat.destroy();
      this.hat = undefined;
    }
    const tex = hatTexture(getHat());
    if (tex) {
      this.hat = this.add.image(this.player.x, this.player.y - 16, tex).setOrigin(0.5, 0.5).setDepth(this.player.depth + 1);
    }
  }

  update(_time: number, delta: number): void {
    if (this.hat) {
      this.hat.setPosition(this.player.x, this.player.y - 16).setFlipX(this.player.flipX).setDepth(this.player.depth + 1);
    }
    if (this.path.length === 0) return;
    const target = this.path[0]!;
    const fx = target.x * TS + TS / 2;
    const fy = target.y * TS + TS;
    const dx = fx - this.player.x;
    const dy = fy - this.player.y;
    const dist = Math.hypot(dx, dy);
    this.lastFacing = faceMove(this, this.player, dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    if (dist <= step || dist === 0) {
      this.player.setPosition(fx, fy);
      this.player.setDepth(fy);
      this.playerTile = { x: target.x, y: target.y };
      this.path.shift();
      if (this.path.length === 0) {
        faceIdle(this, this.player, this.lastFacing);
        const a = this.pendingArrival;
        this.pendingArrival = null;
        a?.();
      }
    } else {
      this.player.x += (dx / dist) * step;
      this.player.y += (dy / dist) * step;
      this.player.setDepth(this.player.y);
    }
  }
}
